// SavedJobs.jsx
// Uses the same auth token system as CandidateProfile & JobsPage
// Calls GET  /api/saved-jobs          (list)
// Calls DELETE /api/saved-jobs        (remove, body: { job_id })
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./SavedJobs.css";

// ── Same auth helpers as CandidateProfile ──
const getToken = () =>
  localStorage.getItem("th_token") || localStorage.getItem("token") || "";

const getUser = () => {
  try {
    const raw = localStorage.getItem("th_user") || localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

const API = " /api";

export default function SavedJobs() {
  const [savedJobs,  setSavedJobs]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState("all");
  const [toast,      setToast]      = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const navigate = useNavigate();

  // ── Guard: must be logged in ──
  useEffect(() => {
    if (!getToken()) {
      navigate("/employee/login");
      return;
    }
    loadSavedJobs();
  }, []); // eslint-disable-line

  async function loadSavedJobs() {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/saved-jobs`, { headers: authHeaders() });
      const data = await res.json();

      if (data.success) {
        setSavedJobs(data.data || []);
      } else {
        showToast(data.error || "Failed to load saved jobs", "error");
      }
    } catch (err) {
      console.error("Error loading saved jobs:", err);
      showToast("Error loading saved jobs. Is the server running?", "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleRemoveSavedJob(jobId, jobTitle) {
    setRemovingId(jobId);
    try {
      const res  = await fetch(`${API}/saved-jobs`, {
        method:  "DELETE",
        headers: authHeaders(),
        body:    JSON.stringify({ job_id: jobId }),
      });
      const data = await res.json();

      if (data.success) {
        // Remove from local state — match on job_id OR id (API returns different shapes)
        setSavedJobs(prev => prev.filter(j => (j.job_id || j.id) !== jobId));
        showToast(`Removed "${jobTitle}" from saved jobs`);
      } else {
        showToast(data.error || "Failed to remove job", "error");
      }
    } catch (err) {
      console.error("Error removing saved job:", err);
      showToast("Error removing job", "error");
    } finally {
      setRemovingId(null);
    }
  }

  function showToast(msg, type = "success") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  // ── Salary helpers ──
  function avgSalary(job) {
    const min = job.min_salary || job.salary_min || 0;
    const max = job.max_salary || job.salary_max || 0;
    return (min + max) / 2;
  }

  function salaryDisplay(job) {
    const min = job.min_salary || job.salary_min || 0;
    const max = job.max_salary || job.salary_max || 0;
    if (min && max) return `₹${Math.round(min/100000)}–${Math.round(max/100000)} LPA`;
    if (min)        return `₹${Math.round(min/100000)}+ LPA`;
    if (job.salary) return job.salary;
    return "Salary not specified";
  }

  // ── Filter ──
  const filteredJobs = filter === "all"
    ? savedJobs
    : savedJobs.filter(job => {
        const avg = avgSalary(job);
        if (filter === "high") return avg > 1000000;
        if (filter === "mid")  return avg >= 300100 && avg <= 1000000;
        if (filter === "low")  return avg > 0 && avg < 300100;
        return true;
      });

  const countByFilter = f => {
    if (f === "all")  return savedJobs.length;
    if (f === "high") return savedJobs.filter(j => avgSalary(j) > 1000000).length;
    if (f === "mid")  return savedJobs.filter(j => { const a=avgSalary(j); return a>=300100&&a<=1000000; }).length;
    if (f === "low")  return savedJobs.filter(j => { const a=avgSalary(j); return a>0&&a<300100; }).length;
    return 0;
  };

  const user = getUser();

  // ── Loading ──
  if (loading) return (
    <div className="saved-jobs loading-container">
      <div className="spinner"/>
      <p>Loading your saved jobs...</p>
    </div>
  );

  return (
    <div className="saved-jobs">

      {/* Toast */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>{toast.msg}</div>
      )}

      {/* Header */}
      <div className="sj-header">
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button
            onClick={() => navigate("/employee/profile")}
            style={{ background:"none", border:"1.5px solid #e0e0e0", borderRadius:9, padding:"7px 14px", cursor:"pointer", fontSize:13, fontWeight:600, display:"flex", alignItems:"center", gap:6, color:"#555" }}>
            ← Back to Profile
          </button>
          <h1>Saved Jobs</h1>
        </div>
        <p className="sj-subtitle">
          {filteredJobs.length} job{filteredJobs.length !== 1 ? "s" : ""}
          {user?.firstName ? ` saved by ${user.firstName}` : " saved"}
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="sj-filters">
        {[
          { key:"all",  label:"All"         },
          { key:"high", label:"High Salary (10L+)" },
          { key:"mid",  label:"Mid (5–10L)"  },
          { key:"low",  label:"Starting Out" },
        ].map(f => (
          <button
            key={f.key}
            className={`filter-btn ${filter === f.key ? "active" : ""}`}
            onClick={() => setFilter(f.key)}>
            {f.label} ({countByFilter(f.key)})
          </button>
        ))}
      </div>

      {/* Empty state */}
      {filteredJobs.length === 0 ? (
        <div className="sj-empty">
          <div className="empty-icon">❤️</div>
          <h2>No saved jobs</h2>
          <p>
            {filter === "all"
              ? "You haven't saved any jobs yet. Find your next opportunity!"
              : "No jobs in this salary range."}
          </p>
          <button className="sj-btn-primary" onClick={() => navigate("/employee/jobs")}>
            Explore Job Listings
          </button>
        </div>
      ) : (
        <div className="sj-grid">
          {filteredJobs.map(job => {
            // Normalise field names — API can return different shapes
            const jobId    = job.job_id   || job.id;
            const title    = job.title    || job.job_title    || "—";
            const company  = job.company  || job.company_name || "Unknown Company";
            const location = job.location || job.job_location || "—";
            const savedAt  = job.saved_at || job.created_at   || null;
            const desc     = job.description || "";

            return (
              <div key={jobId || job.savedId} className="sj-card">
                <div className="sj-card-header">
                  <div className="sj-header-content">
                    <h3 className="sj-job-title">{title}</h3>
                    <p className="sj-company">{company}</p>
                  </div>
                  <button
                    className="sj-btn-unsave"
                    disabled={removingId === jobId}
                    onClick={() => handleRemoveSavedJob(jobId, title)}
                    title="Remove from saved">
                    {removingId === jobId ? "⏳" : "❤️"}
                  </button>
                </div>

                <div className="sj-card-body">
                  <div className="sj-info-row">
                    <span className="sj-icon">📍</span>
                    <span className="sj-text">{location}</span>
                  </div>

                  <div className="sj-info-row">
                    <span className="sj-icon">💰</span>
                    <span className="sj-text">{salaryDisplay(job)}</span>
                  </div>

                  {job.type && (
                    <div className="sj-info-row">
                      <span className="sj-icon">💼</span>
                      <span className="sj-text">{job.type}</span>
                    </div>
                  )}

                  {savedAt && (
                    <div className="sj-info-row">
                      <span className="sj-icon">📅</span>
                      <span className="sj-text">
                        Saved {new Date(savedAt).toLocaleDateString("en-IN", { month:"short", day:"numeric" })}
                      </span>
                    </div>
                  )}

                  {desc && (
                    <div className="sj-description">
                      {desc.substring(0, 120)}{desc.length > 120 ? "…" : ""}
                    </div>
                  )}
                </div>

                <div className="sj-card-footer">
                  <button
                    className="sj-btn-primary"
                    onClick={() => navigate(`/employee/jobs${jobId ? `?highlight=${jobId}` : ""}`)}>
                    View & Apply
                  </button>
                  <button
                    className="sj-btn-secondary"
                    disabled={removingId === jobId}
                    onClick={() => handleRemoveSavedJob(jobId, title)}>
                    {removingId === jobId ? "Removing…" : "Remove"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}